/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Brick, registerBrick, WorkflowObjectState, CloudObject } from 'olympe';
import {combineLatest} from 'rxjs';
import {getLogger} from "@olympeio/core";
import { queryObjectState } from './_workflow';

export default class WorkflowGetObjectState extends Brick {

    /**
     * @override
     */
    setupExecution($) {
        // Propagate null values if an input is cleared
        return combineLatest(this.getInputs().map(i => $.observe(i)));
    }

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!CloudObject} object
     * @param {!Workflow} workflow
     * @param {function(WorkflowObjectState)} setStateObject
     * @param {function(String)} setState
     * @param {function(Date)} setDate
     * @param {function(String)} setAssignee
     * @param {function(CloudObject)} setSerializedObject
     */
    update($, [object], [setStateObject, setState, setDate, setAssignee]) {
        // If the object or the workflow becomes null, propagate the info
        if (object === null) {
            getLogger('Get object state').error('No objects received');
            setStateObject(null);
            setState(null);
            setDate(null);
            setAssignee(null);
        } else {
            queryObjectState(object)
                .observe($)
                .subscribe(queryResult => {
                    const stateObject = queryResult.getFirst();
                    setStateObject(stateObject);
                    setState(stateObject?.get(WorkflowObjectState.stateProp));
                    setDate(stateObject?.get(WorkflowObjectState.dateTimeProp));
                    setAssignee(stateObject?.get(WorkflowObjectState.assigneeProp));
                });
        }
    }
}

registerBrick('0180dc81678a30700598', WorkflowGetObjectState);
